import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  AlertTriangle,
  FileText,
  Activity,
  MessageSquare,
  Save,
  X,
  History,
  UserPlus,
  Check,
  ChevronsUpDown,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { type ContactUI } from "@/types/ui/customer-detail";
import { useUsersList } from "@/hooks/useUsersData";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { type TransactionType } from "@/types/transaction";
import { formatCurrency } from "@/lib/utils/formatters";

interface CreateCaseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaseCreated?: () => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
}

interface NewCaseData {
  customerName: string;
  customerId: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed' | 'Escalated';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

interface CustomerOption {
  id: string;
  name: string;
  accountNumber: string;
}

interface Transaction {
  id: string;
  invoiceNumber: string;
  invoiceDate: Date;
  dueDate: Date;
  type: TransactionType;
  amount: number;
  balance: number;
  status: 'Outstanding' | 'Overdue';
  daysOverdue: number;
}

export function CreateCaseDrawer({ open, onOpenChange, onCaseCreated, prePopulatedCustomer }: CreateCaseDrawerProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [, setNewNoteContent] = useState("");
  const [contacts, setContacts] = useState<ContactUI[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch active users for the Assigned To dropdown
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');
  
  // Mock customers data - in a real app, this would come from an API
  const [availableCustomers] = useState<CustomerOption[]>([
    { id: "1", name: "Acme Corporation", accountNumber: "ACC-001" },
    { id: "2", name: "Global Tech Industries", accountNumber: "ACC-002" },
    { id: "3", name: "Mega Retail Group", accountNumber: "ACC-003" },
    { id: "4", name: "Premier Manufacturing", accountNumber: "ACC-004" },
    { id: "5", name: "Sunrise Enterprises", accountNumber: "ACC-005" },
    { id: "6", name: "Quantum Solutions", accountNumber: "ACC-006" },
    { id: "7", name: "Atlantic Trading Co", accountNumber: "ACC-007" },
    { id: "8", name: "Pacific Imports", accountNumber: "ACC-008" },
    { id: "9", name: "Northern Logistics", accountNumber: "ACC-009" },
    { id: "10", name: "Summit Distribution", accountNumber: "ACC-010" },
    { id: "11", name: "Coastal Ventures", accountNumber: "ACC-011" },
    { id: "12", name: "Metro Services", accountNumber: "ACC-012" },
    { id: "13", name: "Alpine Industries", accountNumber: "ACC-013" },
    { id: "14", name: "Horizon Enterprises", accountNumber: "ACC-014" },
    { id: "15", name: "Vertex Solutions", accountNumber: "ACC-015" },
  ]);
  
  // Available collection strategies
  const availableStrategies = [
    { id: "strategy-1", name: "Standard Collection", type: "Standard" },
    { id: "strategy-2", name: "Intensive Follow-up", type: "Intensive" },
    { id: "strategy-3", name: "VIP Treatment", type: "VIP" },
    { id: "strategy-4", name: "High Risk Protocol", type: "High Risk" },
  ];

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerId: prePopulatedCustomer?.accountNumber || "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    strategyId: "strategy-1", // Default to Standard Collection
    assignedTo: undefined,
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  // Mock available transactions for selection
  const [availableTransactions] = useState<Transaction[]>([
    {
      id: "1",
      invoiceNumber: "INV-2024-001",
      invoiceDate: new Date(2024, 0, 15),
      dueDate: new Date(2024, 1, 15),
      type: "invoice",
      amount: 6000,
      balance: 6000,
      status: "Overdue",
      daysOverdue: 45,
    },
    {
      id: "2",
      invoiceNumber: "INV-2024-002",
      invoiceDate: new Date(2024, 1, 1),
      dueDate: new Date(2024, 2, 1),
      type: "invoice",
      amount: 5250,
      balance: 5250,
      status: "Overdue",
      daysOverdue: 30,
    },
    {
      id: "3",
      invoiceNumber: "INV-2024-003",
      invoiceDate: new Date(2024, 2, 10),
      dueDate: new Date(2024, 3, 10),
      type: "invoice",
      amount: 3750,
      balance: 3750,
      status: "Outstanding",
      daysOverdue: 0,
    },
  ]);

  // Mock contacts for the customer
  const mockContacts: ContactUI[] = [
    {
      id: "contact-1",
      firstName: "John",
      surname: "Smith",
      email: "john.smith@example.com",
      phone: "+1 (555) 123-4567",
      jobTitle: "Accounts Payable Manager",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Primary contact for all payment-related matters",
      createdAt: new Date(2023, 0, 15).toISOString(),
      lastContact: new Date(2024, 2, 20).toISOString(),
      isPrimary: true,
    },
    {
      id: "contact-2",
      firstName: "Sarah",
      surname: "Johnson",
      email: "sarah.johnson@example.com",
      phone: "+1 (555) 123-4568",
      jobTitle: "Finance Director",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Escalation contact for urgent matters",
      createdAt: new Date(2023, 1, 10).toISOString(),
      lastContact: new Date(2024, 2, 15).toISOString(),
      isPrimary: false,
    },
    {
      id: "contact-3",
      firstName: "Michael",
      surname: "Chen",
      email: "michael.chen@example.com",
      phone: "+1 (555) 123-4569",
      jobTitle: "Accounts Payable Clerk",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Day-to-day invoice queries",
      createdAt: new Date(2023, 3, 5).toISOString(),
      lastContact: new Date(2024, 2, 18).toISOString(),
      isPrimary: false,
    },
  ];

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = 
      formData.customerEmail.trim() !== "" ||
      formData.customerPhone.trim() !== "" ||
      formData.notes.trim() !== "" ||
      selectedTransactions.size > 0 ||
      formData.priority !== "Medium" ||
      formData.status !== "Active" ||
      formData.strategyId !== "strategy-1" ||
      formData.assignedTo !== undefined ||
      formData.nextAction !== "Follow-up call";
    
    setHasUnsavedChanges(hasChanges);
  }, [formData, selectedTransactions]);

  // Handle customer selection from combobox
  const handleCustomerSelect = (customerId: string) => {
    const customer = availableCustomers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customerId: customer.accountNumber,
        customerName: customer.name,
      });
      setCustomerComboboxOpen(false);
      setHasUnsavedChanges(true);
    }
  };

  // Reset form when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Check for unsaved changes before closing
      if (hasUnsavedChanges) {
        setShowCloseConfirmation(true);
        return;
      }
      
      // Reset form
      setFormData({
        customerName: prePopulatedCustomer?.customerName || "",
        customerId: prePopulatedCustomer?.accountNumber || "",
        customerEmail: "",
        customerPhone: "",
        priority: "Medium",
        status: "Active",
        strategyId: "strategy-1", // Reset to default
        assignedTo: undefined,
        notes: "",
        nextAction: "Follow-up call",
        nextActionDate: format(new Date(), "yyyy-MM-dd"),
      });
      setSelectedTransactions(new Set());
      setNewNoteContent("");
      setActiveTab("overview");
      setSelectedContactId("");
      setContacts(mockContacts);
      setCustomerComboboxOpen(false);
      setHasUnsavedChanges(false);
    } else {
      // Load contacts when opening
      setContacts(mockContacts);
      
      // Auto-populate primary contact if customer is pre-populated
      if (prePopulatedCustomer) {
        const primaryContact = mockContacts.find((c) => c.isPrimary);
        if (primaryContact) {
          setSelectedContactId(primaryContact.id);
          setFormData({
            customerName: prePopulatedCustomer.customerName,
            customerId: prePopulatedCustomer.accountNumber,
            customerEmail: primaryContact.email,
            customerPhone: primaryContact.phone,
            priority: "Medium",
            status: "Active",
            strategyId: "strategy-1",
            assignedTo: undefined,
            notes: "",
            nextAction: "Follow-up call",
            nextActionDate: format(new Date(), "yyyy-MM-dd"),
          });
        }
      }
    }
    onOpenChange(open);
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setHasUnsavedChanges(false);
    
    // Reset form
    setFormData({
      customerName: prePopulatedCustomer?.customerName || "",
      customerId: prePopulatedCustomer?.accountNumber || "",
      customerEmail: "",
      customerPhone: "",
      priority: "Medium",
      status: "Active",
      strategyId: "strategy-1",
      assignedTo: undefined,
      notes: "",
      nextAction: "Follow-up call",
      nextActionDate: format(new Date(), "yyyy-MM-dd"),
    });
    setSelectedTransactions(new Set());
    setNewNoteContent("");
    setActiveTab("overview");
    setSelectedContactId("");
    setContacts(mockContacts);
    setCustomerComboboxOpen(false);
    
    onOpenChange(false);
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContactId(contactId);
    
    if (contactId === "none") {
      // Clear email and phone when "None" is selected
      setFormData({
        ...formData,
        customerEmail: "",
        customerPhone: "",
      });
    } else {
      // Find the selected contact and auto-populate
      const selectedContact = contacts.find((c) => c.id === contactId);
      if (selectedContact) {
        setFormData({
          ...formData,
          customerEmail: selectedContact.email,
          customerPhone: selectedContact.phone,
        });
      }
    }
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const validateForm = (): boolean => {
    // Required fields validation
    if (!formData.customerId.trim() || !formData.customerName.trim()) {
      toast.error("Please select a customer");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerEmail.trim()) {
      toast.error("Customer email is required");
      setActiveTab("overview");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) {
      toast.error("Please enter a valid email address");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerPhone.trim()) {
      toast.error("Customer phone is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("transactions");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Calculate total amount from selected transactions
      const totalAmount = availableTransactions
        .filter(t => selectedTransactions.has(t.id))
        .reduce((sum, t) => sum + t.balance, 0);

      // Here you would make the actual API call to create the case
      console.log("Creating new case:", {
        ...formData,
        selectedTransactions: Array.from(selectedTransactions),
        totalAmount,
      });

      toast.success("Collection case created successfully");
      
      // Call the callback to refresh the queue
      if (onCaseCreated) {
        onCaseCreated();
      }

      // Close the drawer
      handleOpenChange(false);
    } catch (error) {
      toast.error("Failed to create case. Please try again.");
      console.error("Error creating case:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const calculateTotalAmount = () => {
    return availableTransactions
      .filter(t => selectedTransactions.has(t.id))
      .reduce((sum, t) => sum + t.balance, 0);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div>
                <SheetTitle className="text-3xl font-bold tracking-tight">Create New Case</SheetTitle>
                <p className="text-muted-foreground mt-1">
                  Enter customer and case information to create a new collection case
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="default"
                onClick={handleSaveCase}
                disabled={isSaving}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Creating..." : "Create Case"}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 mx-6 mt-4 shrink-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 pb-6 mt-6">
            <TabsContent value="overview" className="space-y-6 mt-0">
              {/* Two Column Layout - Customer & Case Info */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Customer Information & Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and customer profile</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {/* Customer Selection */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer <span className="text-red-500">*</span></p>
                        {prePopulatedCustomer ? (
                          <div className="space-y-2">
                            <p className="text-lg font-semibold">{formData.customerId} {formData.customerName}</p>
                            <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                          </div>
                        ) : (
                          <Popover open={customerComboboxOpen} onOpenChange={setCustomerComboboxOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                id="customer"
                                variant="outline"
                                role="combobox"
                                aria-expanded={customerComboboxOpen}
                                className="w-full justify-between"
                              >
                                {formData.customerId && formData.customerName
                                  ? `${formData.customerId} ${formData.customerName}`
                                  : "Select a customer..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search customers..." />
                                <CommandList>
                                  <CommandEmpty>No customer found.</CommandEmpty>
                                  <CommandGroup>
                                    {availableCustomers.map((customer) => (
                                      <CommandItem
                                        key={customer.id}
                                        value={`${customer.accountNumber} ${customer.name}`}
                                        onSelect={() => handleCustomerSelect(customer.id)}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            formData.customerId === customer.accountNumber
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {customer.accountNumber} {customer.name}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      
                      <Separator />
                      
                      {/* Contact Name */}
                      <div className="space-y-2">
                        <Label htmlFor="contact" className="text-xs font-medium text-muted-foreground">
                          Contact Name
                        </Label>
                        <Select
                          value={selectedContactId}
                          onValueChange={handleContactChange}
                        >
                          <SelectTrigger id="contact">
                            <SelectValue placeholder="Select a contact to auto-populate email and phone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {contacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {`${contact.firstName} ${contact.surname}`}
                                {contact.isPrimary && " (Primary)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      {/* Email Address */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.customerEmail}
                          onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                          placeholder="customer@example.com"
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.customerPhone}
                          onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Case Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Case Management
                    </CardTitle>
                    <CardDescription>Case details and configuration</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-6 md:grid-cols-2">
                      {/* First Column */}
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Outstanding Amount</p>
                          <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                            £{calculateTotalAmount().toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">From selected transactions</p>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="strategyId" className="text-xs font-medium text-muted-foreground">
                            Collection Strategy
                          </Label>
                          <Select
                            value={formData.strategyId}
                            onValueChange={(value) => setFormData({ ...formData, strategyId: value })}
                          >
                            <SelectTrigger id="strategyId">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableStrategies.map((strategy) => (
                                <SelectItem key={strategy.id} value={strategy.id}>
                                  {strategy.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {availableStrategies.find(s => s.id === formData.strategyId)?.type}
                          </p>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground">
                            Assigned To
                          </Label>
                          <Select
                            value={formData.assignedTo || "unassigned"}
                            onValueChange={(value) => 
                              setFormData({ ...formData, assignedTo: value === "unassigned" ? undefined : value })
                            }
                          >
                            <SelectTrigger id="assignedTo">
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {activeUsers.map((user) => (
                                <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                  {user.firstName} {user.lastName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                            Case Status
                          </Label>
                          <Select
                            value={formData.status}
                            onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                              setFormData({ ...formData, status: value })
                            }
                          >
                            <SelectTrigger id="status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Escalated">Escalated</SelectItem>
                              <SelectItem value="On Hold">On Hold</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                            Priority Level
                          </Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                              setFormData({ ...formData, priority: value })
                            }
                          >
                            <SelectTrigger id="priority">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="High">High</SelectItem>
                              <SelectItem value="Medium">Medium</SelectItem>
                              <SelectItem value="Low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Second Column - Empty for now, matching Case Details structure */}
                      <div className="space-y-3">
                        {/* This column is intentionally left empty to match the Case Details layout */}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Action - Call to Action Style */}
              <Card className="border-2 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Next Scheduled Action
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="nextAction" className="text-xs font-medium text-muted-foreground">
                        Action Type <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.nextAction}
                        onValueChange={(value) => setFormData({ ...formData, nextAction: value })}
                      >
                        <SelectTrigger id="nextAction">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                          <SelectItem value="Send email">Send email</SelectItem>
                          <SelectItem value="Send letter">Send letter</SelectItem>
                          <SelectItem value="Payment reminder">Payment reminder</SelectItem>
                          <SelectItem value="Escalate to manager">Escalate to manager</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nextActionDate" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Due Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nextActionDate"
                        type="date"
                        value={formData.nextActionDate}
                        onChange={(e) => setFormData({ ...formData, nextActionDate: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Case Notes
                  </CardTitle>
                  <CardDescription>Add notes about this case</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add notes about this case..."
                    rows={4}
                    className="resize-none"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Outstanding Transactions</CardTitle>
                      <CardDescription>
                        Select transactions to include in this case ({selectedTransactions.size} selected)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Reference Number</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {availableTransactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            selectedTransactions.has(transaction.id) && "bg-muted/30"
                          )}
                          onClick={() => handleToggleTransaction(transaction.id)}
                        >
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTransactions.has(transaction.id)}
                              onCheckedChange={() => handleToggleTransaction(transaction.id)}
                              aria-label={`Select ${transaction.invoiceNumber}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {transaction.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            {format(transaction.invoiceDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            {format(transaction.dueDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            <TransactionTypeBadge type={transaction.type} />
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.balance)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "Overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transaction.daysOverdue > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {transaction.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(calculateTotalAmount())}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    Activity history will appear here after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activity yet</p>
                    <p className="text-xs mt-1">Activities will be logged once the case is created</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Audit trail will be available after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit entries yet</p>
                    <p className="text-xs mt-1">All case modifications will be logged after creation</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving? All your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
